import React, { useState } from 'react';
import { Layout, PaginationWrapper, TableCell } from '../../index.styled';
import {
  LoadingIndicator,
  Table,
  TableContextProvider,
  TableFooter,
  TablePaginationNew,
  TablePaginationSizeNew,
} from 'lib/components';
import { EmptySearchText, EmptySearchWrapper } from '../styles';
import { EmptySearchIcon } from '../assets';
import { Flex, Gap } from 'lib/components/styles/layout';
import { Button } from 'react-covideo-common';
import { HiPencil, HiTrash } from 'react-icons/hi';
import { BsLayersFill } from 'react-icons/bs';
import { FaChevronRight } from 'react-icons/fa6';
import { useHistory } from 'react-router';
import {
  ILandingPageDesignGroup,
  ILandingPageDesignGroupWithLandingPages,
  LP_BUILDER_MODAL_STEP,
} from '../types';
import DeleteGroupModal from '../modals/DeleteGroupModal';
import CreateEditGroupModal from '../modals/CreateEditGroupModal';
import {
  LP_BUILDER_FORM_MODE,
  LP_BUILDER_MODAL_STEPS,
  LpBuilderFormMode,
} from '../constants';
import { LandingPageBuilderModalForm } from '../components/modal/LandingPageBuilderModalForm';
import { useEditBatchTemplatesInGroup } from 'lib/api/superadmin/landingPageDesignGroups/useEditBatchTemplatesInGroup';
import { ILandingPageBuilderBulkEditFormData } from '../helpers/getInitialLandingPageValuesForBulkEdit';
import { usePrepareLandingPageBulkData } from '../helpers/usePrepareLandingPageBulkData';
import { SuperAdminRole } from 'lib/const/SuperAdminRole';
import { useAuth } from 'lib/context';
import { useTheme } from 'styled-components/macro';

const TABLE_NAMES = {
  groupName: 'Group Name',
  lpCount: '# Landing Pages',
  landingPages: 'Landing Pages',
  actions: '',
};

const columnWidths = [150, 120, 300];

const tableFields = [
  {
    value: 'title',
    label: TABLE_NAMES.groupName,
  },
  {
    value: 'landingPageDesignCount',
    label: TABLE_NAMES.lpCount,
  },
  {
    value: 'landingPageDesigns',
    label: TABLE_NAMES.landingPages,
  },
];

type LandingPageGroupTableProps = {
  isLoading: boolean;
  data: ILandingPageDesignGroupWithLandingPages[];
  count: number;
  page: number;
  onPaginationChange: ({
    page,
    size,
  }: {
    page: number;
    size: number;
  }) => Promise<void>;
  limit: number;
  searchQuery: string;
};

export const LandingPageGroupTable = ({
  isLoading,
  data,
  count,
  page,
  limit,
  searchQuery,
  onPaginationChange,
}: LandingPageGroupTableProps) => {
  const { userData } = useAuth();
  const theme = useTheme();
  const { prepareBulkData, isPreparingBulkData } =
    usePrepareLandingPageBulkData();
  const history = useHistory();
  const isResellerAdmin = userData.isResellerAdmin;
  const isCovideoSuperAdmin =
    userData.superAdminRole === SuperAdminRole.COVIDEO_SUPER_ADMIN;

  const [groupToDelete, setGroupToDelete] =
    useState<ILandingPageDesignGroup | null>(null);
  const [groupToEdit, setGroupToEdit] =
    useState<ILandingPageDesignGroupWithLandingPages | null>(null);
  const [group, setGroup] = useState<ILandingPageDesignGroup | null>(null);

  // form mode
  const [modalState, setModalState] = useState<{
    mode: LpBuilderFormMode;
    open: boolean;
  }>({
    mode: LP_BUILDER_FORM_MODE.CREATE,
    open: false,
  });
  const [step, setStep] = useState<LP_BUILDER_MODAL_STEP>(
    LP_BUILDER_MODAL_STEPS.design
  );
  const [previewLandingPageId, setPreviewLandingPageId] = useState(0);

  const onCloseLandingPageBuilderModalHandler = async () => {
    setGroup(null);
    setStep(LP_BUILDER_MODAL_STEPS.design);
    setModalState(s => ({ ...s, open: false }));
  };

  // BULK EDIT Design Mutation
  const {
    mutateAsync: bulkEditDesignMutation,
    isLoading: isLoadingBulkEditDesignMutation,
  } = useEditBatchTemplatesInGroup(onCloseLandingPageBuilderModalHandler);

  const isSubmitting = isLoadingBulkEditDesignMutation || isPreparingBulkData;

  const onBulkEditSubmit = async (
    values: ILandingPageBuilderBulkEditFormData
  ) => {
    if (group === null) {
      return;
    }
    const { landingPageDesignGroupId } = group;

    const data = await prepareBulkData(values);

    await bulkEditDesignMutation({
      groupId: landingPageDesignGroupId,
      data,
    });
  };

  const handleOpenBulkEditModal = (group: ILandingPageDesignGroup) => {
    setGroup(group);
    setModalState({
      mode: LP_BUILDER_FORM_MODE.BULK_EDIT,
      open: true,
    });
  };

  const getTableRowData = () => {
    return data.map(group => {
      const lpDesignLimit =
        group.landingPageDesigns.length < 6
          ? group.landingPageDesigns.length
          : 6;
      const lpDesignCount = group.landingPageDesigns.length;
      const lpDesignTitle = lpDesignCount
        ? group.landingPageDesigns
            .slice(0, lpDesignLimit)
            .map(lpDesign => lpDesign.title)
            .join(', ')
        : '-';

      return {
        key: `group-row-${group.landingPageDesignGroupId}`,
        columns: [
          <TableCell width={120} style={{ display: 'block' }}>
            {group.title}
          </TableCell>,
          <TableCell>{lpDesignCount}</TableCell>,

          <Flex
            flexDirection='row'
            alignItems='center'
            justifyContent='space-between'
          >
            <TableCell width={400} style={{ display: 'block' }}>
              {lpDesignTitle}
            </TableCell>

            <Gap gap='8px'>
              <Button
                variant='destructive'
                onClick={() => setGroupToDelete(group)}
                icon={<HiTrash size={20} />}
              />
              <Button
                variant='secondary'
                onClick={() => setGroupToEdit(group)}
                icon={<HiPencil size={20} />}
              />
              <Button
                onClick={() => handleOpenBulkEditModal(group)}
                disabled={!group.landingPageDesigns.length}
                icon={<BsLayersFill />}
              />
              <Button
                variant='transparent'
                onClick={() =>
                  history.push(
                    `/admin/landing-pages-v2/manage-groups/${group.landingPageDesignGroupId}`
                  )
                }
                icon={<FaChevronRight />}
              />
            </Gap>
          </Flex>,
        ],
        onClick: () => {},
      };
    });
  };

  if (isLoading) {
    return (
      <Flex height='300px'>
        <LoadingIndicator isLoading={isLoading} />
      </Flex>
    );
  }

  return (
    <Layout>
      {data.length === 0 ? (
        <>
          <EmptySearchWrapper>
            <EmptySearchIcon
              fill={theme.colors.primary[100]}
              stroke={theme.colors.primary[100]}
            />
            <EmptySearchText>
              {searchQuery?.trim()
                ? 'Sorry we couldn’t find what you were looking for. Please try again.'
                : 'There are no groups available.'}
            </EmptySearchText>
          </EmptySearchWrapper>
        </>
      ) : (
        <TableContextProvider
          total={count}
          initPage={page}
          initSize={limit}
          onChange={onPaginationChange}
        >
          {
            <Table
              compact={true}
              relative={false}
              headers={tableFields.map((item, index) => {
                return (
                  <TableCell
                    key={index}
                    width={columnWidths[index]}
                    onClick={() => {}}
                  >
                    {item.label}
                  </TableCell>
                );
              })}
              rows={getTableRowData()}
            />
          }

          {!!groupToDelete && (
            <DeleteGroupModal
              group={groupToDelete}
              onClose={() => setGroupToDelete(null)}
            />
          )}

          {!!groupToEdit && (
            <CreateEditGroupModal
              group={groupToEdit}
              onClose={() => setGroupToEdit(null)}
            />
          )}

          {modalState.open && group && (
            <LandingPageBuilderModalForm
              onSubmit={values =>
                onBulkEditSubmit(values as ILandingPageBuilderBulkEditFormData)
              }
              isSubmitting={isSubmitting}
              mode={modalState.mode}
              group={group}
              selectedTemplateDesignId={null}
              previewLandingPageId={previewLandingPageId}
              setPreviewLandingPageId={setPreviewLandingPageId}
              setStep={setStep}
              step={step}
              onClose={onCloseLandingPageBuilderModalHandler}
              isResellerAdmin={isResellerAdmin}
              isCovideoSuperAdmin={isCovideoSuperAdmin}
            />
          )}

          <PaginationWrapper>
            <TableFooter>
              <TablePaginationNew />
              <TablePaginationSizeNew text='Show rows:' />
            </TableFooter>
          </PaginationWrapper>
        </TableContextProvider>
      )}
    </Layout>
  );
};
